import React from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import ElapsedTime from 'app/components/general/ElapsedTime';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {useIsScreenLargerThan} from 'app/general/mediaqueries';
import {StyledDocItem} from 'app/procedures/styles';
import {PreviousVersionIndicator} from 'app/signoffs/components/PreviousVersionIndicator';
import {SignoffStatusWithButton} from 'app/signoffs/components/SignoffStatusWithButton';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {
  GroupedSignoffSerializedType,
  SignoffDocumentSerializedType,
  SignoffSerializedType,
  TeamSerializedType,
} from 'sweet-types';

interface Props {
  groupedBy: GroupedSignoffSerializedType['group_type'];
  object: SignoffSerializedType;
  team: TeamSerializedType;
}

export const TeamSignoffsListItem = ({groupedBy, object}: Props) => {
  const isBigScreen = useIsScreenLargerThan('sm');
  return (
    <StyledDocItem className="row no-gutters border-bottom py-2">
      <div className="col-12 col-sm d-flex align-items-center flex-wrap">
        <div className="py-1 pr-1 d-flex align-items-center">
          {groupedBy === 'doc' ? (
            <>
              <UserAvatar
                small={true}
                className="mr-2"
                user={object.user}
                tooltipPlacement={'right'}
                showCard
              />
              <span className="text-dark font-weight-bold small">
                {object.user.name || object.user.email || gt('Unnamed Person')}
              </span>
            </>
          ) : groupedBy === 'user' ? (
            <FullHTMLLink
              html_url={object.html_url}
              className="text-dark font-weight-bold small"
            >
              <i
                className={`${getContentType(object.content_type).icon} mr-2`}
              />
              {object.name}
            </FullHTMLLink>
          ) : null}
        </div>
        <PreviousVersionIndicator signoff={object} />
      </div>
      <div className="col-12 col-sm-auto text-muted font-italic small">
        <div
          className={cs('d-flex align-items-center font-weight-bold', {
            'justify-content-end': isBigScreen,
          })}
        >
          <div>
            <SignoffStatusWithButton
              signoff={object}
              alignment={isBigScreen ? 'right' : 'left'}
              hidePreviousVersionBadge
            />
          </div>
        </div>
        {object.last_request_sent_at != null ? (
          <StyledTinyText className={cs({'text-right': isBigScreen})}>
            <ElapsedTime
              wrapTooltip={(last_request_sent_at) =>
                object.created_at < object.last_request_sent_at
                  ? gt('Reminder sent %s', last_request_sent_at)
                  : gt('Request sent %s', last_request_sent_at)
              }
              timestamp={object.last_request_sent_at}
              ContainerTag="span"
              wrapText={
                object.created_at < object.last_request_sent_at
                  ? gt('Reminder sent %s')
                  : gt('Request sent %s')
              }
            />
          </StyledTinyText>
        ) : null}
      </div>
    </StyledDocItem>
  );
};

const StyledTinyText = styled.div`
  font-size: 8pt;
`;

interface DocSignoffProps {
  object: SignoffDocumentSerializedType;
}

export const DocumentSignoffsListItem = ({object}: DocSignoffProps) => {
  const isBigScreen = useIsScreenLargerThan('sm');
  return (
    <StyledDocItem className="row no-gutters border-bottom py-2">
      <div className="col-12 col-sm d-flex align-items-center">
        <FullHTMLLink
          html_url={object.html_url}
          className="text-dark font-weight-bold"
        >
          <i className={`${getContentType(object.content_type).icon} mr-2`} />
          {object.name}
        </FullHTMLLink>
      </div>
      <div className="col-12 col-sm-auto text-muted font-italic d-flex align-items-center small font-weight-bold">
        <div>
          <SignoffStatusWithButton
            signoff={object}
            alignment={isBigScreen ? 'right' : 'left'}
            hidePreviousVersionBadge
          />
        </div>
      </div>
    </StyledDocItem>
  );
};
